import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  oauthRequestApiRef,
  oktaAuthApiRef,
} from '@backstage/frontend-plugin-api';
import { OktaAuth } from '@backstage/core-app-api';
import { AWSCredentials, OktaToken } from '@eng-portal/czi-extensions-react';
import {
  CatalogApi,
  catalogApiRef,
  entityPresentationApiRef,
  EntityRefPresentation,
  EntityPresentationApi,
} from '@backstage/plugin-catalog-react';
import { DefaultEntityPresentationApi } from '@backstage/plugin-catalog';
import type { ComponentType } from 'react';
import { Entity } from '@backstage/catalog-model';
import CloudIcon from '@mui/icons-material/Cloud';
import GrainIcon from '@mui/icons-material/Grain';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  AWS_ACCOUNT_RESOURCE_TYPE,
  EKS_CLUSTER_RESOURCE_TYPE,
  GITHUB_ACTION_RESOURCE_TYPE,
  GITHUB_REPOSITORY_RESOURCE_TYPE,
  ROUTE53_ZONE_RESOURCE_TYPE,
  RDS_CLUSTER_RESOURCE_TYPE,
} from '@eng-portal/czi-extensions-common';
import { GitHubIcon } from '@backstage/core-components';
import DnsIcon from '@mui/icons-material/Dns';
import StorageIcon from '@mui/icons-material/Storage';
import { ApiBlueprint } from '@backstage/frontend-plugin-api';
import type { ExtensionDefinition } from '@backstage/frontend-plugin-api';

class CustomEntityPresentationApi implements EntityPresentationApi {
  private readonly defaultApi: EntityPresentationApi;

  constructor({ catalogApi }: { catalogApi: CatalogApi }) {
    this.defaultApi = DefaultEntityPresentationApi.create({
      catalogApi,
    });
  }

  forEntity(entityOrRef: Entity, context?: { defaultKind?: string; defaultNamespace?: string; }): EntityRefPresentation {
    const defaultPresentation = this.defaultApi.forEntity(entityOrRef, context);
    const icon = this.getIcon(entityOrRef);

    if (icon) {
      defaultPresentation.snapshot.Icon = icon;
    }
    return {
      snapshot: defaultPresentation.snapshot,
      promise: Promise.resolve(defaultPresentation.snapshot),
    };

  }

  getIcon(entity: Entity): ComponentType<{}> | undefined | false {
    // Logic based on kind and spec.type
    if (entity.kind === 'Resource') {
      switch (entity.spec?.type as string | undefined) {
        case EKS_CLUSTER_RESOURCE_TYPE:
          return GrainIcon; // a collection of grains is like a collection of nodes
        case AWS_ACCOUNT_RESOURCE_TYPE:
          return CloudIcon;
        case GITHUB_ACTION_RESOURCE_TYPE:
          return CheckCircleOutlineIcon;
        case GITHUB_REPOSITORY_RESOURCE_TYPE:
          return GitHubIcon;
        case ROUTE53_ZONE_RESOURCE_TYPE:
          return DnsIcon;
        case RDS_CLUSTER_RESOURCE_TYPE:
          return StorageIcon;
      }
    }

    return false;
  }
}

export const scmIntegrationsApi: ExtensionDefinition = ApiBlueprint.make({
  name: 'scm-integrations',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: scmIntegrationsApiRef,
        deps: { configApi: configApiRef },
        factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
      }),
    ),
});

export const scmAuthApi: ExtensionDefinition = ApiBlueprint.make({
  name: 'scm-auth',
  params: defineParams => defineParams(ScmAuth.createDefaultApiFactory()),
});

export const oktaTokenApi: ExtensionDefinition = ApiBlueprint.make({
  name: 'okta-token',
  params: defineParams => defineParams(OktaToken.createDefaultApiFactory()),
});

export const awsCredentialsApi: ExtensionDefinition = ApiBlueprint.make({
  name: 'aws-credentials',
  params: defineParams => defineParams(AWSCredentials.createDefaultApiFactory()),
});

export const oktaAuthApi: ExtensionDefinition = ApiBlueprint.make({
  name: 'okta-auth',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: oktaAuthApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          oauthRequestApi: oauthRequestApiRef,
          configApi: configApiRef,
        },
        factory: ({ discoveryApi, oauthRequestApi, configApi }) => {
          const environment = configApi.getOptionalString('auth.environment');
          return OktaAuth.create({
            discoveryApi,
            oauthRequestApi,
            environment,
            defaultScopes: ['openid', 'email', 'profile', 'offline_access', 'groups'],
          });
        },
      }),
    ),
});

export const entityPresentationApi: ExtensionDefinition = ApiBlueprint.make({
  name: 'entity-presentation',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: entityPresentationApiRef,
        deps: { catalogApi: catalogApiRef },
        factory: ({ catalogApi }) => new CustomEntityPresentationApi({ catalogApi }),
      }),
    ),
});

export const apiExtensions: ExtensionDefinition[] = [
  scmIntegrationsApi,
  scmAuthApi,
  oktaTokenApi,
  awsCredentialsApi,
  oktaAuthApi,
];

export const catalogApiExtensions: ExtensionDefinition[] = [
  entityPresentationApi,
];
